import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Group, Stack, Text, Tooltip } from '@mantine/core';
import ClientBadge from './ClientBadge.tsx';

dayjs.extend(relativeTime);

/**
 * A device as a person would name it: "samsung SM-A715F · Android 13".
 * Some phones send their whole build fingerprint along
 * ("samsung/a71naxx/a71:13/TP1A…/release-keys"); that part is left out.
 */
export const shortDevice = (device: string) => {
  const all = device.split(/\s+/).filter(Boolean);
  const words = all.filter((word) => !word.includes('/') && word.length <= 24);
  // Where the fingerprint stood in for "Android", the version is what is left.
  const version = words.length < all.length && /^\d+(\.\d+)*$/.test(words[words.length - 1] || '') ? words.pop() : '';
  const name = words.join(' ').replace(/\s*·\s*$/, '').trim();
  const short = version ? `${name} · Android ${version}` : name;
  return short || device.slice(0, 40);
};

const PLATFORMS: Record<string, string> = { android: 'Android', ios: 'iOS' };

/** "3 hours ago", with the exact time on hover. */
export const SeenAt = ({ at, size = 'xs' }: { at?: string | null; size?: string }) =>
  at ? (
    <Tooltip label={dayjs(at).format('DD/MM/YYYY HH:mm:ss')}>
      <Text size={size} c="dimmed" span style={{ cursor: 'default' }}>
        {dayjs(at).fromNow()}
      </Text>
    </Tooltip>
  ) : (
    <Text size={size} c="dimmed" span>
      Never
    </Text>
  );

/** What is known about one employee's use of one client. */
export const ClientUsage = ({
  client,
  usage,
}: {
  client: 'app' | 'web';
  usage: any;
}) => {
  if (!usage) {
    return (
      <Text size="sm" c="dimmed">
        Never used
      </Text>
    );
  }

  const details = [
    usage.version && `v${usage.version}`,
    // The web's platform is always "web"; only the app's says something.
    client === 'app' && (PLATFORMS[usage.platform] || usage.platform),
  ].filter(Boolean);

  return (
    <Stack gap={4}>
      <Group gap={6} wrap="nowrap">
        <ClientBadge client={client} legacy={usage.legacy} size="sm" />
        {details.length > 0 && (
          <Text size="xs" fw={500}>
            {details.join(' · ')}
          </Text>
        )}
      </Group>
      {usage.device && (
        // The short name; the full text, as the device sent it, on hover.
        <Tooltip label={usage.device} multiline maw={360} disabled={shortDevice(usage.device) === usage.device}>
          <Text size="xs" c="dimmed" lineClamp={1} maw={260}>
            {shortDevice(usage.device)}
          </Text>
        </Tooltip>
      )}
      <Text size="xs" c="dimmed">
        <SeenAt at={usage.last_seen_at} /> ·{' '}
        <Tooltip
          label={`First seen ${dayjs(usage.first_seen_at).format('DD/MM/YYYY HH:mm')}`}
        >
          <Text span inherit style={{ cursor: 'default' }}>
            {usage.request_count} request{usage.request_count === 1 ? '' : 's'}
          </Text>
        </Tooltip>
      </Text>
    </Stack>
  );
};
